import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Clapperboard, Copy, FileInput, Lock, Plus, Search, Trash2 } from 'lucide-react';
import { ImportNleDialog } from '../../components/CheckIn';
import { CUSTOM_SEQUENCE_RATES, SEQUENCE_FORMATS, SEQUENCE_FORMAT_GROUPS, aspectLabel, formatDuration, frameRateLabel, sequenceSizeProblem } from '@prism/contracts';
import { api, errorMessage } from '../../api/client';
import { useDuplicateSequence, useMe, useSequences, type SequenceSummary } from '../../api/hooks';
import { Modal } from '../../components/Modal';
import { toast } from '../../components/Toasts';
import { EmptyState } from '../../components/EmptyState';
import { relative } from '../../lib/format';
import { queryKeys } from '../../api/queryKeys';
import { lockSince } from './LockBanner';
import { LoadMore } from '../../components/LoadMore';
import { RowMenu } from '../../components/RowMenu';

/** The New sequence dialog's formats (1.0.0-rc.11, contracts SEQUENCE_FORMATS), grouped, and a custom size. */
const CUSTOM = 'custom';

export function CreateHome() {
  const me = useMe().data!;
  const q = useSequences();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [name, setName] = useState('');
  const [format, setFormat] = useState(SEQUENCE_FORMATS[0]!.id);
  const [custom, setCustom] = useState({ width: '1080', height: '1920', rate: 1 });
  const customSize = { width: Number(custom.width), height: Number(custom.height) };
  const customProblem = format === CUSTOM ? sequenceSizeProblem(customSize.width, customSize.height) : null;
  const canEdit = me.permissions.includes('sequence.edit');
  const [search, setSearch] = useState('');
  const [scope, setScope] = useState<'all' | 'mine'>('all');

  async function create() {
    if (customProblem) { toast(customProblem, 'error'); return; }
    try {
      const f = format === CUSTOM
        ? { frameRate: CUSTOM_SEQUENCE_RATES[custom.rate]!, ...customSize }
        : SEQUENCE_FORMATS.find((x) => x.id === format) ?? SEQUENCE_FORMATS[0]!;
      const s = await api<SequenceSummary>('/api/v1/sequences', { method: 'POST', json: { name: name.trim() || 'Untitled sequence', frameRate: f.frameRate, width: f.width, height: f.height } });
      await qc.invalidateQueries({ queryKey: queryKeys.sequences() });
      navigate(`/create/${s.id}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  // 1.7.4: Duplicate in the row's actions: a copy of the saved sequence, opened at once.
  const duplicateSequence = useDuplicateSequence();
  async function duplicate(s: SequenceSummary) {
    try {
      const copy = await duplicateSequence(s.id);
      toast(`Duplicated as “${copy.name}”`);
      navigate(`/create/${copy.id}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  async function remove(s: SequenceSummary) {
    if (!window.confirm(`Delete the sequence “${s.name}”? It disappears from Create and from collections. Its finished exports are kept.`)) return;
    try {
      await api(`/api/v1/sequences/${s.id}`, { method: 'DELETE' });
      await qc.invalidateQueries({ queryKey: queryKeys.sequences() });
      toast(`Deleted “${s.name}”`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  const everything = q.data?.items ?? [];
  const needle = search.trim().toLowerCase();
  const items = everything.filter((s) =>
    (scope === 'all' || s.createdById === me.user.id) &&
    (!needle || s.name.toLowerCase().includes(needle) || s.description.toLowerCase().includes(needle)));
  return (
    <>
      <header className="topbar">
        <h1><Clapperboard size={16} color="var(--accent-strong)" /> Create</h1>
        <div className="topbar-spacer" />
        {canEdit && <button className="btn ghost" onClick={() => setImporting(true)} data-testid="import-nle-button" title="A timeline from Premiere, Resolve, Final Cut or Avid as a new sequence"><FileInput size={15} /> Import NLE sequence…</button>}
        {canEdit && <button className="btn primary" onClick={() => setOpen(true)} data-testid="new-sequence"><Plus size={15} /> New sequence</button>}
      </header>
      {importing && <ImportNleDialog onClose={() => setImporting(false)} />}
      <div className="page">
        {everything.length > 0 && (
          <div className="list-filters" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
            <div className="search" role="search" style={{ maxWidth: 360 }}>
              <Search size={15} />
              <input className="input" placeholder="Search names and descriptions…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search sequences" data-testid="sequence-search" />
            </div>
            <div className="segmented" role="group" aria-label="Show">
              <button aria-pressed={scope === 'all'} onClick={() => setScope('all')} data-testid="seq-scope-all">All</button>
              <button aria-pressed={scope === 'mine'} onClick={() => setScope('mine')} data-testid="seq-scope-mine">My sequences</button>
            </div>
            {(needle || scope === 'mine') && <span className="muted" data-testid="sequence-count">{items.length} of {everything.length}</span>}
          </div>
        )}
        {everything.length === 0 && !q.isLoading ? (
          <EmptyState icon={<Clapperboard size={24} />} title="Make it a story">
            <p>Assemble clips from the library on a simple timeline, add a lower third and subtitles, and export a finished file with a record of exactly what went into it.</p>
            {canEdit && <button className="btn primary" onClick={() => setOpen(true)}><Plus size={15} /> New sequence</button>}
          </EmptyState>
        ) : items.length === 0 ? (
          <p className="muted" data-testid="sequence-none">No sequences match.</p>
        ) : (
          <table className="table" data-testid="sequence-list">
            <thead><tr><th>Name</th><th>Owner</th><th>Duration</th><th>Format</th><th>Clips</th><th>Updated</th>{canEdit && <th aria-label="Actions" />}</tr></thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} tabIndex={0} onClick={() => navigate(`/create/${s.id}`)} onKeyDown={(e) => e.key === 'Enter' && navigate(`/create/${s.id}`)}>
                  <td className="wrap" style={{ minWidth: 180 }}>
                    <div style={{ fontWeight: 560, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      {s.name}
                      {s.lockedBy && (
                        <span className="pill lock-pill" data-testid="sequence-lock" title={`Being edited by ${s.lockedBy.displayName} since ${lockSince(s.lockedBy.since)}`}>
                          <Lock size={11} /> {s.lockedBy.displayName}
                        </span>
                      )}
                    </div>
                    {s.description && <div className="muted" style={{ fontSize: 12, maxWidth: 520, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.description}>{s.description}</div>}
                  </td>
                  <td className="muted" data-testid="sequence-owner">{s.createdBy ?? '—'}</td>
                  <td className="mono">{formatDuration(s.durationTicks)}</td>
                  <td className="muted">{s.width}×{s.height} · {frameRateLabel(s.frameRate)} fps</td>
                  <td className="muted">{s.clips}</td>
                  <td className="muted">{relative(s.updatedAt)}{s.updatedBy ? ` · ${s.updatedBy}` : ''}</td>
                  {canEdit && (
                    <td style={{ textAlign: 'right' }} onClick={(e) => e.stopPropagation()}>
                      <RowMenu label={`Actions for ${s.name}`} testId="sequence-row-menu" items={[
                        { key: 'duplicate', label: <><Copy size={13} /> Duplicate</>, onSelect: () => void duplicate(s) },
                      ]} />
                      <button className="btn ghost sm icon danger" aria-label={`Delete ${s.name}`} title="Delete sequence" data-testid="delete-sequence" onClick={() => void remove(s)}><Trash2 size={13} /></button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <LoadMore list={q} shown={everything.length} noun="sequences" />
      </div>
      {open && (
        <Modal title="New sequence" onClose={() => setOpen(false)} actions={<><button className="btn ghost" onClick={() => setOpen(false)}>Cancel</button><button className="btn primary" onClick={() => void create()} data-testid="create-sequence">Create</button></>}>
          <div className="field"><label htmlFor="seq-name">Name</label><input id="seq-name" className="input" autoFocus value={name} placeholder="Harbour story" onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void create()} /></div>
          <div className="field"><label htmlFor="seq-format">Format</label>
            <select id="seq-format" className="select" value={format} onChange={(e) => setFormat(e.target.value)} data-testid="seq-format">
              {SEQUENCE_FORMAT_GROUPS.map((g) => (
                <optgroup key={g.id} label={g.label}>
                  {SEQUENCE_FORMATS.filter((f) => f.group === g.id).map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </optgroup>
              ))}
              <optgroup label="Custom"><option value={CUSTOM}>Custom size…</option></optgroup>
            </select>
            <span className="help">The frame rate is the timing grid for edits and exports; sources at other rates are conformed. A sequence is not progressive or interlaced: the export format decides (an interlaced export of a 25 fps sequence is 1080i50).</span>
          </div>
          {format === CUSTOM && (
            <div className="field-row" data-testid="custom-format">
              <div className="field"><label htmlFor="seq-w">Width</label><input id="seq-w" className="input" type="number" min={16} max={7680} step={2} value={custom.width} onChange={(e) => setCustom({ ...custom, width: e.target.value })} data-testid="seq-width" /></div>
              <div className="field"><label htmlFor="seq-h">Height</label><input id="seq-h" className="input" type="number" min={16} max={4320} step={2} value={custom.height} onChange={(e) => setCustom({ ...custom, height: e.target.value })} data-testid="seq-height" /></div>
              <div className="field"><label htmlFor="seq-rate">Frame rate</label>
                <select id="seq-rate" className="select" value={custom.rate} onChange={(e) => setCustom({ ...custom, rate: Number(e.target.value) })} data-testid="seq-rate">
                  {CUSTOM_SEQUENCE_RATES.map((r, i) => <option key={i} value={i}>{frameRateLabel(r)} fps</option>)}
                </select>
              </div>
            </div>
          )}
          {format === CUSTOM && <span className="help" style={customProblem ? { color: 'var(--danger)' } : undefined} data-testid="custom-format-help">{customProblem ?? `${customSize.width}×${customSize.height} · ${aspectLabel(customSize)}`}</span>}
        </Modal>
      )}
    </>
  );
}
